// PRD.305 §12 — Laboratory Settings.
//
// Storage rides `app_config.extra_json.laboratory`, the same codegen-free
// pattern as `clinic_classification` (PRD.282), `main_modules` (PRD.283) and
// `queue_workflow` (PRD.302): no migration, no PowerSync schema change, no
// syncWrite registry change. `app_config` already carries an extra_json
// text→jsonb coercion and server-injected updated_by/updated_at.
//
// The one MVP setting is Patient Workspace Access. It is deliberately NOT in
// Clinic Settings, Patient Workspace settings or Online Booking — laboratory
// workflow configuration belongs to Laboratory.
//
// Reads never throw: a missing key, malformed JSON or an unrecognised mode all
// resolve to the default, so every existing clinic gets safe behaviour with no
// migration (PRD §20).
//
// Self-contained apart from the intra-package status/mode types, so it runs
// under the node:test harness.

import {
  DEFAULT_LABORATORY_ACCESS,
  LABORATORY_ACCESS_MODES,
  type LaboratoryPatientWorkspaceAccess,
} from './laboratory-workflow.js';

/** Structural shape of the source row — AppConfigRow satisfies this. */
export interface LaboratorySettingsSource {
  extra_json?: string | null;
}

export interface LaboratorySettings {
  /** How much laboratory management Patient Workspace offers (PRD §8.1). */
  patientWorkspaceAccess: LaboratoryPatientWorkspaceAccess;
}

/** PRD §12 / §20 — Monitor + Results is the default for every clinic. */
export const DEFAULT_LABORATORY_SETTINGS: LaboratorySettings = {
  patientWorkspaceAccess: DEFAULT_LABORATORY_ACCESS,
};

/** The `app_config.extra_json` key this configuration lives under. */
export const LABORATORY_SETTINGS_KEY = 'laboratory';

/**
 * Parse `extra_json` (text from PowerSync SQLite, object from the server) into
 * a plain object. Falls back to {} on any malformed input. Kept local so this
 * module stays free of workspace-package imports, matching main-modules.ts and
 * queue-workflow.ts.
 */
function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (typeof extraJson === 'object' && !Array.isArray(extraJson)) {
    return extraJson as Record<string, unknown>;
  }
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* fall through */
    }
  }
  return {};
}

/**
 * Normalize an arbitrary/untrusted `laboratory` value into canonical settings.
 * Unrecognised modes fall back to the default rather than throwing — a clinic
 * must never be locked out of its own laboratory by a bad stored value.
 */
export function normalizeLaboratorySettings(raw: unknown): LaboratorySettings {
  const obj =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const mode = obj.patientWorkspaceAccess;
  const valid =
    typeof mode === 'string' && (LABORATORY_ACCESS_MODES as readonly string[]).includes(mode);
  return {
    patientWorkspaceAccess: valid
      ? (mode as LaboratoryPatientWorkspaceAccess)
      : DEFAULT_LABORATORY_SETTINGS.patientWorkspaceAccess,
  };
}

/** Read Laboratory Settings from an app_config row. Never throws. */
export function getLaboratorySettings(
  row: LaboratorySettingsSource | null | undefined,
): LaboratorySettings {
  const extra = parseExtraJson(row?.extra_json);
  return normalizeLaboratorySettings(extra[LABORATORY_SETTINGS_KEY]);
}

/** True when the clinic has explicitly saved settings (vs. running on defaults). */
export function hasSavedLaboratorySettings(
  row: LaboratorySettingsSource | null | undefined,
): boolean {
  const extra = parseExtraJson(row?.extra_json);
  const value = extra[LABORATORY_SETTINGS_KEY];
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Serialize a settings change into an updated `extra_json` string, preserving
 * every other top-level key (regional_settings, clinic_classification,
 * main_modules, queue_workflow, print_branding, and any future key).
 */
export function writeLaboratorySettings(
  currentExtraJson: unknown,
  settings: LaboratorySettings,
): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  next[LABORATORY_SETTINGS_KEY] = normalizeLaboratorySettings(settings);
  return JSON.stringify(next);
}
